/**
 * Admin Guard Component
 * Protects admin routes with multiple security checks
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import adminApiService from '../services/adminApiService';

const AdminGuard = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [mfaVerified, setMfaVerified] = useState(false);
  
  useEffect(() => {
    const checkAdminAccess = async () => {
      if (!isAuthenticated) {
        navigate('/login?redirect=admin');
        return;
      }
      
      // Check if user has admin role
      const userIsAdmin = user?.is_admin || (user?.roles || []).includes('admin');
      
      if (!userIsAdmin) {
        // Log unauthorized access attempt
        adminApiService.logSecurityEvent('UNAUTHORIZED_ADMIN_ACCESS', {
          username: user?.username,
          user_id: user?.id
        });
        
        navigate('/dashboard?error=admin_required');
        return;
      }
      
      // Validate admin access with backend
      const validation = await adminApiService.validateAdminAccess();
      
      if (!validation.valid) {
        navigate('/login?error=admin_validation_failed');
        return;
      }
      
      // Check MFA requirement
      if (user?.mfa_required && !mfaVerified) {
        // MFA verification will be handled by MFA component
        setLoading(false);
        setIsAdmin(true);
        return;
      }
      
      setIsAdmin(true);
      setLoading(false);
    };
    
    checkAdminAccess();
  }, [isAuthenticated, user, navigate, mfaVerified]);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-400">Verifying admin access...</p>
          <p className="mt-2 text-sm text-gray-500">Security checks in progress</p>
        </div>
      </div>
    );
  }
  
  if (!isAdmin) {
    return null; // Navigation will handle redirect
  }
  
  return children;
};

export default AdminGuard;
